// Optional account: sign in with your email and a 6-digit code (server/auth.ts). Signing in turns on the cloud
// backup (cloud.ts). Everything else in the app works the same without an account.
import type { AccountInfo } from "../shared/model.ts";
import { $, $maybe, el, input, toast, twoTap } from "./dom.ts";
import { LANG, fmtWhen, t } from "./i18n.ts";
import { cancelButton, closeSheet, grow, openSheet, primaryButton } from "./sheet.ts";
import { onRender } from "./state.ts";
import { lsGet, lsJson, lsSet } from "./util.ts";
import { cloudBackupNow, cloudRestore, cloudBusy, offerRestore } from "./cloud.ts";
import { identify, mode as billingMode, setWebStatus } from "./billing.ts";

/** The session token (only on this phone) and the account as last seen, so the panel shows offline too. */
let token = lsGet("session", "");
export let account: AccountInfo | null = token ? lsJson<AccountInfo | null>("account", null) : null;
export const signedIn = (): boolean => !!token;

function setAccount(a: AccountInfo | null): void {
  account = a;
  lsSet("account", JSON.stringify(a));
  renderAccount();
  // purchases follow the account: tie them to it in the app, and show them on the website
  if (a?.id) void identify(a.id);
  if (billingMode === "web") void (a ? refreshWebPlus() : setWebStatus(null));
}
async function refreshWebPlus(): Promise<void> {
  try {
    const res = await accountApi("/api/billing/me");
    if (res.ok) await setWebStatus((await res.json()) as Parameters<typeof setWebStatus>[0]);
  } catch { /* offline: keep what we had */ }
}
function forgetSession(): void {
  token = "";
  lsSet("session", "");
  setAccount(null);
}

/** A signed-in call to the server. A 401 means the session was ended elsewhere: sign out here too. */
export async function accountApi(path: string, init: RequestInit = {}, timeoutMs = 30000): Promise<Response> {
  const res = await fetch(path, { ...init, headers: { ...(init.headers as Record<string, string>), authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(timeoutMs) });
  if (res.status === 401 && token) forgetSession();
  return res;
}
/** Fetches the account (and the latest cloud backup's summary) from the server. */
export async function refreshAccount(): Promise<AccountInfo | null> {
  if (!token || !navigator.onLine) return account;
  try {
    const res = await accountApi("/api/account");
    if (res.ok) setAccount((await res.json()) as AccountInfo);
  } catch { /* offline: keep what we had */ }
  return account;
}

/* ---------- signing in ---------- */
const post = (path: string, body: object) =>
  fetch(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), signal: AbortSignal.timeout(30000) });

/** The sign-in sheet. `onDone` runs after a successful sign-in (e.g. the club board loads the club area). */
let afterSignIn: (() => void) | null = null;
export function openSignIn(onDone?: () => void): void {
  afterSignIn = onDone ?? null;
  openSheet(t("signInTitle"), [
    el("p", { class: "hint", text: t("accountIntro") }),
    el("label", { class: "f" }, el("span", { class: "lbl", text: t("signInEmail") }),
      el("input", { type: "email", id: "si-email", autocomplete: "email", inputmode: "email", placeholder: t("signInEmailPh"), value: account?.email ?? "" })),
    el("label", { class: "toggle" }, el("input", { type: "checkbox", id: "si-news" }), el("span", { text: t("signInNews") })),
    el("p", { class: "hint warn", id: "si-msg", hidden: true }),
  ], [grow(), cancelButton(), primaryButton(t("signInSendCode"), (btn) => void sendCode(btn))]);
  setTimeout(() => $maybe("#si-email")?.focus(), 50);
}
function say(text: string, warn = true): void {
  const m = $maybe("#si-msg");
  if (!m) return;
  m.textContent = text; m.hidden = false; m.className = warn ? "hint warn" : "hint";
}

async function sendCode(btn: HTMLButtonElement): Promise<void> {
  const email = input("#si-email").value.trim().toLowerCase(), news = input("#si-news").checked;
  if (!/^\S+@\S+\.\S+$/.test(email)) { say(t("signInBadEmail")); return; }
  btn.disabled = true;
  try {
    const res = await post("/api/auth/start", { email, lang: LANG });
    const body = (await res.json().catch(() => ({}))) as { error?: string; retryIn?: number; devCode?: string };
    if (res.ok) { openCodeStep(email, news, body.devCode); return; }
    say(res.status === 400 ? t("signInBadEmail") : res.status === 503 ? t("signInUnavailable")
      : body.error === "wait" ? t("signInWait", body.retryIn ?? 60) : res.status === 429 ? t("signInTooMany") : t("signInFailed"));
  } catch { say(t("signInFailed")); }
  btn.disabled = false;
}

function openCodeStep(email: string, news: boolean, devCode?: string): void {
  openSheet(t("signInTitle"), [
    el("p", { text: t("signInCodeSent", email) }),
    el("label", { class: "f" }, el("span", { class: "lbl", text: t("signInCode") }),
      el("input", { type: "text", id: "si-code", inputmode: "numeric", autocomplete: "one-time-code", maxlength: "6", pattern: "[0-9]*", class: "codeinput", value: devCode ?? "" })),
    el("p", { class: "hint warn", id: "si-msg", hidden: true }),
    el("button", { class: "linkbtn", type: "button", onclick: async (e) => {
      const b = e.currentTarget;
      b.disabled = true;
      const res = await post("/api/auth/start", { email, lang: LANG }).catch(() => null);
      const body = (await res?.json().catch(() => ({}))) as { retryIn?: number } | undefined;
      say(res?.ok ? t("signInCodeSent", email) : res?.status === 429 ? t("signInWait", body?.retryIn ?? 60) : t("signInFailed"), !res?.ok);
      b.disabled = false;
    } }, t("signInResend")),
  ], [grow(), cancelButton(), primaryButton(t("accountSignIn"), (btn) => void verifyCode(btn, email, news))]);
  const field = input("#si-code");
  field.addEventListener("input", () => { if (field.value.replace(/\D/g, "").length === 6) $<HTMLButtonElement>("#gsheet-foot .btn.primary").click(); });
  setTimeout(() => field.focus(), 50);
}

async function verifyCode(btn: HTMLButtonElement, email: string, news: boolean): Promise<void> {
  const code = input("#si-code").value.replace(/\D/g, "");
  if (code.length !== 6 || btn.disabled) return;
  btn.disabled = true;
  try {
    const res = await post("/api/auth/verify", { email, code, news, lang: LANG });
    const body = (await res.json().catch(() => ({}))) as { token?: string; account?: AccountInfo; error?: string; left?: number };
    if (res.ok && body.token && body.account) {
      token = body.token;
      lsSet("session", token);
      setAccount(body.account);
      closeSheet();
      toast(t("signedIn"));
      afterSignIn?.(); afterSignIn = null;
      // a backup in the cloud and an empty phone: offer it; otherwise back up what is here
      if (!(await offerRestore())) void cloudBackupNow(false);
      return;
    }
    say(body.error === "wrong code" ? t("signInWrongCode", body.left ?? 0) : body.error === "expired" ? t("signInExpired") : t("signInFailed"));
  } catch { say(t("signInFailed")); }
  btn.disabled = false;
}

/* ---------- the Account panel under More ---------- */
export function renderAccount(): void {
  const box = $maybe("#account-panel");
  if (!box) return;
  const title = el("h2", { text: t("accountTitle") });
  if (!signedIn() || !account) {
    box.replaceChildren(title, el("p", { text: t("accountIntro") }),
      el("div", { class: "btns" }, el("button", { class: "btn primary", type: "button", onclick: () => openSignIn() }, t("accountSignIn"))));
    return;
  }
  const a = account, b = a.backup;
  const restore = twoTap(el("button", { class: "btn", type: "button" }, t("cloudRestore")), () => void cloudRestore(), () => t("cloudRestore"), () => t("cloudRestoreConfirm"));
  restore.disabled = !b || cloudBusy();
  const del = twoTap(el("button", { class: "btn danger small", type: "button" }, t("accountDelete")), () => void deleteAccount(), () => t("accountDelete"), () => t("accountDeleteConfirm"));
  box.replaceChildren(title,
    el("p", { class: "accountline" }, el("b", { text: t("accountAs", a.email) })),
    el("p", { class: "num", id: "cloud-status", text: b ? t("cloudLast", fmtWhen(b.at), b.catches, b.pins) : t("cloudNone") }),
    el("p", { class: "hint", text: t("cloudAuto") }),
    el("div", { class: "btns" },
      el("button", { class: "btn primary", type: "button", disabled: cloudBusy(), onclick: () => void cloudBackupNow(true) }, t("cloudNow")), restore),
    el("label", { class: "pushrow" },
      el("span", { class: "pushtext" }, el("b", { text: t("accountNews") }), el("small", { text: t("accountNewsText") })),
      el("input", { type: "checkbox", class: "switch", checked: a.news, onchange: (e) => void setNews(e.currentTarget) })),
    el("div", { class: "btns" }, el("button", { class: "btn small", type: "button", onclick: () => void signOut() }, t("accountSignOut")), del));
}

async function setNews(box: HTMLInputElement): Promise<void> {
  try {
    const res = await accountApi("/api/account", { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify({ news: box.checked }) });
    if (!res.ok) throw new Error(String(res.status));
    setAccount((await res.json()) as AccountInfo);
  } catch { box.checked = !box.checked; toast(t("signInFailed")); }
}
async function signOut(): Promise<void> {
  await accountApi("/api/auth/logout", { method: "POST" }).catch(() => {});
  forgetSession();
  toast(t("signedOut"));
}
async function deleteAccount(): Promise<void> {
  try {
    const res = await accountApi("/api/account", { method: "DELETE" });
    if (!res.ok) throw new Error(String(res.status));
    forgetSession();
    toast(t("accountDeleted"));
  } catch { toast(t("signInFailed")); }
}

onRender(renderAccount);
export function accountStart(): void {
  renderAccount();
  void refreshAccount();
}
